import {TodoRepeatUnit, TodoStatus, type Repeat} from '@cp/model';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import useCreateTodo from '../../hooks/todo/useCreateTodo';
import useTodos from '../../hooks/todo/useTodos';
import useUpdateTodo from '../../hooks/todo/useUpdateTodo';

const DAYS_OF_WEEK = [
  {value: 0, label: 'Sunday'},
  {value: 1, label: 'Monday'},
  {value: 2, label: 'Tuesday'},
  {value: 3, label: 'Wednesday'},
  {value: 4, label: 'Thursday'},
  {value: 5, label: 'Friday'},
  {value: 6, label: 'Saturday'},
];

interface TodoFormProps {
  open: boolean;
  todoId: string | null;
  onClose: () => void;
  uid?: string;
}

export default function TodoForm({open, todoId, onClose, uid}: TodoFormProps) {
  const {allTodos} = useTodos(uid);
  const {createTodo, loading: creating} = useCreateTodo(uid);
  const {updateTodo, loading: updating} = useUpdateTodo(uid);

  const editingTodo = useMemo(() => (todoId ? allTodos.find((t) => t.id === todoId) : null), [todoId, allTodos]);

  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [status, setStatus] = useState<TodoStatus>(TodoStatus.ACTIVE);
  const [hasRepeat, setHasRepeat] = useState(false);
  const [repeatUnit, setRepeatUnit] = useState<TodoRepeatUnit>(TodoRepeatUnit.DAY);
  const [repeatNumber, setRepeatNumber] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    allTodos.forEach((todo) => {
      todo.tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [allTodos]);

  useEffect(() => {
    if (editingTodo) {
      setTitle(editingTodo.title);
      setNote(editingTodo.note ?? '');
      setTags(editingTodo.tags);
      setStatus(editingTodo.status);
      setHasRepeat(editingTodo.repeat !== null);
      if (editingTodo.repeat) {
        setRepeatUnit(editingTodo.repeat.unit as TodoRepeatUnit);
        setRepeatNumber(editingTodo.repeat.number ?? 1);
        setDaysOfWeek(editingTodo.repeat.daysOfWeek ?? []);
      }
    } else {
      setTitle('');
      setNote('');
      setTags([]);
      setStatus(TodoStatus.ACTIVE);
      setHasRepeat(false);
      setRepeatUnit(TodoRepeatUnit.DAY);
      setRepeatNumber(1);
      setDaysOfWeek([]);
    }
  }, [editingTodo, open]);

  const handleSubmit = async () => {
    if (!title.trim()) return;

    // Validate day-of-week selection
    if (hasRepeat && repeatUnit === TodoRepeatUnit.DAY_OF_WEEK && daysOfWeek.length === 0) {
      alert('Please select at least one day of the week');
      return;
    }

    try {
      let repeat: Repeat | null = null;

      if (hasRepeat) {
        repeat = {
          unit: repeatUnit,
          startDate: editingTodo?.repeat?.startDate ?? new Date(),
          ...(repeatUnit === TodoRepeatUnit.DAY_OF_WEEK ? {daysOfWeek} : {number: repeatNumber}),
        };
      }

      if (editingTodo) {
        const updateData = {
          title: title.trim(),
          note: note.trim() || undefined,
          tags,
          status,
          repeat,
        };

        console.log('Updating todo:', editingTodo.id, updateData);

        try {
          await updateTodo(editingTodo.id, updateData);
          console.log('Todo updated successfully');
          onClose();
        } catch (updateError) {
          console.error('Update error:', updateError);
          throw updateError; // Re-throw to be caught by outer catch
        }
      } else {
        await createTodo({
          title: title.trim(),
          note: note.trim() || undefined,
          tags,
          status,
          done: null,
          repeat: repeat ?? undefined,
        });
        onClose();
      }
    } catch (err) {
      console.error('Failed to save todo:', err);
      alert(`Failed to save todo: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const loading = creating || updating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingTodo ? 'Edit Todo' : 'Add Todo'}</DialogTitle>
      <DialogContent>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 2}}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            disabled={loading}
          />

          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={loading}
          />

          <Autocomplete
            multiple
            freeSolo
            options={allTags}
            value={tags}
            onChange={(_, newValue) => setTags(newValue)}
            renderInput={(params) => <TextField {...params} label="Tags" disabled={loading} />}
            disabled={loading}
          />

          <FormControl fullWidth disabled={loading}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value as TodoStatus)} label="Status">
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archive">Archive</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={hasRepeat} onChange={(e) => setHasRepeat(e.target.checked)} disabled={loading} />}
            label="Repeat"
          />

          {hasRepeat && (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel>Repeat Type</InputLabel>
                <Select
                  value={repeatUnit}
                  onChange={(e) => {
                    const newUnit = e.target.value as TodoRepeatUnit;
                    setRepeatUnit(newUnit);
                    if (newUnit !== TodoRepeatUnit.DAY_OF_WEEK) {
                      setDaysOfWeek([]);
                    }
                  }}
                  label="Repeat Type"
                >
                  <MenuItem value="day">Every N Days</MenuItem>
                  <MenuItem value="week">Every N Weeks</MenuItem>
                  <MenuItem value="month">Every N Months</MenuItem>
                  <MenuItem value="year">Every N Years</MenuItem>
                  <MenuItem value="dayOfWeek">Specific Days of Week</MenuItem>
                </Select>
              </FormControl>

              {repeatUnit === 'dayOfWeek' ? (
                <Box>
                  <Typography variant="body2" sx={{mb: 1}}>
                    Select days of the week:
                  </Typography>
                  <FormGroup>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                      {DAYS_OF_WEEK.map((day) => (
                        <FormControlLabel
                          key={day.value}
                          control={
                            <Checkbox
                              checked={daysOfWeek.includes(day.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setDaysOfWeek([...daysOfWeek, day.value]);
                                } else {
                                  setDaysOfWeek(daysOfWeek.filter((d) => d !== day.value));
                                }
                              }}
                              disabled={loading}
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </Box>
                  </FormGroup>
                </Box>
              ) : (
                <TextField
                  label="Number"
                  type="number"
                  value={repeatNumber}
                  onChange={(e) => setRepeatNumber(Number(e.target.value))}
                  inputProps={{min: 1}}
                  disabled={loading}
                  fullWidth
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !title.trim() || (hasRepeat && repeatUnit === 'dayOfWeek' && daysOfWeek.length === 0)}
        >
          {editingTodo ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
